import { decodeJwtPayload } from '../utils/jwt';

// WebSocket Service для real-time взаимодействия
class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.listeners = new Map();
    this.reconnectInterval = null;
    this.heartbeatInterval = null;
    this.isConnected = false;
  }

  connect(token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    // Проверяем срок действия токена
    try {
      const payload = decodeJwtPayload(token);
      if (!payload) {
        throw new Error('Invalid token payload');
      }
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        console.warn('Token expired, refreshing...');
        // Токен истек, нужно обновить
        this.handleTokenExpired();
        return;
      }
    } catch (error) {
      console.error('Error parsing token:', error);
    }

    const wsUrl = `${process.env.REACT_APP_WS_URL || 'wss://api.travmap.org'}/ws?token=${token}`;
    console.log('Connecting to WebSocket:', wsUrl);
    
    this.ws = new WebSocket(wsUrl);
    
    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.clearReconnectInterval();
      this.startHeartbeat();
      
      // Уведомляем слушателей о подключении
      this.notifyListeners('connection', { status: 'connected' });
    };
    
    this.ws.onmessage = (event) => {
      try {
        // Проверяем, что данные являются строкой
        if (typeof event.data !== 'string') {
          console.warn('WebSocket received non-string data:', typeof event.data);
          return;
        }

        // Обрабатываем возможные множественные JSON объекты (разделенные \n)
        // Бэкенд может отправлять несколько JSON объектов в одном сообщении, разделяя их символом новой строки
        const data = event.data.trim();
        if (!data) {
          return;
        }

        // Разбиваем по новым строкам и обрабатываем каждый JSON объект отдельно
        const lines = data.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) {
            continue; // Пропускаем пустые строки
          }

          try {
            const message = JSON.parse(line);
            // console.log('WebSocket message received:', message);
            this.handleMessage(message);
          } catch (parseError) {
            // Если это единственная строка и парсинг не удался - логируем ошибку
            if (lines.length === 1) {
              console.error('Error parsing WebSocket message:', {
                error: parseError.message,
                dataLength: data.length,
                dataPreview: data.substring(0, 100),
                dataSuffix: data.length > 100 ? '...' : '',
                fullError: parseError
              });
            } else {
              // Пропускаем эту строку, но продолжаем обработку других
              console.warn('Error parsing WebSocket message line:', {
                lineIndex: i + 1,
                totalLines: lines.length,
                error: parseError.message,
                linePreview: line.substring(0, 100)
              });
            }
          }
        }
      } catch (error) {
        // Если даже разбиение не помогло, логируем полную ошибку
        console.error('Error processing WebSocket message:', {
          error: error.message,
          dataType: typeof event.data,
          dataLength: event.data?.length || 0,
          dataPreview: typeof event.data === 'string' ? event.data.substring(0, 200) : 'N/A',
          fullError: error
        });
      }
    };
    
    this.ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      this.isConnected = false;
      this.stopHeartbeat();
      
      // Уведомляем слушателей об отключении
      this.notifyListeners('connection', { status: 'disconnected' });
      
      // Попытка переподключения только если это не было намеренное закрытие
      if (event.code !== 1000) {
        this.handleReconnect();
      }
    };
    
    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.notifyListeners('error', { error: error.message || 'WebSocket error' });
    };
  }

  handleMessage(message) {
    // Обрабатываем разные типы сообщений
    switch (message.type) {
      case 'notification':
        this.notifyListeners('notification', message);
        break;
      case 'new_comment':
        this.notifyListeners('new_comment', message);
        break;
      case 'new_like':
        this.notifyListeners('new_like', message);
        break;
      case 'new_follow':
        this.notifyListeners('new_follow', message);
        break;
      case 'new_message':
        this.notifyListeners('new_message', message);
        break;
      case 'messages_read':
        this.notifyListeners('messages_read', message);
        break;
      case 'COMMENT_CREATED':
        this.notifyListeners('COMMENT_CREATED', message);
        break;
      case 'COMMENT_UPDATED':
        this.notifyListeners('COMMENT_UPDATED', message);
        break;
      case 'COMMENT_DELETED':
        this.notifyListeners('COMMENT_DELETED', message);
        break;
      case 'COMMENT_LIKED':
        this.notifyListeners('COMMENT_LIKED', message);
        break;
      case 'COMMENT_UNLIKED':
        this.notifyListeners('COMMENT_UNLIKED', message);
        break;
      case 'user_online':
        this.notifyListeners('user_online', message);
        break;
      case 'user_offline':
        this.notifyListeners('user_offline', message);
        break;
      case 'typing':
        this.notifyListeners('typing', message);
        break;
      case 'pong':
        // Ответ на ping - соединение активно
        break;
      case 'friend_request':
        this.notifyListeners('friend_request', message);
        break;
      case 'friend_request_response':
        this.notifyListeners('friend_request_response', message);
        break;
      case 'friend_accepted':
        this.notifyListeners('friend_accepted', message);
        break;
      case 'friend_rejected':
        this.notifyListeners('friend_rejected', message);
        break;
      case 'friendship_status_updated':
        this.notifyListeners('friendship_status_updated', message);
        break;
      case 'friend_removed':
        this.notifyListeners('friend_removed', message);
        break;
      default:
        console.log('Unknown message type:', message.type);
    }
  }

  notifyListeners(eventType, data) {
    const listeners = this.listeners.get(eventType) || [];
    listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in WebSocket listener:', error);
      }
    });
  }

  subscribe(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
    
    // Возвращаем функцию для отписки
    return () => this.unsubscribe(eventType, callback);
  }

  unsubscribe(eventType, callback) {
    const listeners = this.listeners.get(eventType) || [];
    const index = listeners.indexOf(callback);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }

  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
      
      this.reconnectInterval = setTimeout(() => {
        const token = localStorage.getItem('token');
        if (token) {
          this.connect(token);
        }
      }, delay);
    } else {
      console.error('Max reconnection attempts reached');
      this.notifyListeners('error', { error: 'Connection lost. Please refresh the page.' });
    }
  }

  clearReconnectInterval() {
    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  }

  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Ping каждые 30 секунд
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  sendMessage(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  disconnect() {
    this.clearReconnectInterval();
    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.isConnected = false;
    this.listeners.clear();
  }

  handleTokenExpired() {
    console.log('Token expired, attempting to refresh...');
    // Уведомляем слушателей о необходимости обновить токен
    this.notifyListeners('token_expired', { message: 'Token expired, please refresh the page' });
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }
}

export default new WebSocketService();
